import { Button, Dialog, DropdownMenu, IconButton, Text } from '@radix-ui/themes';
import { UserIcon } from 'lucide-react';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import LocaleContext from '../hooks/LocaleContext';

const User = ({ asSubmenu = false }: { asSubmenu?: boolean }) => {
  const { t } = useTranslation();
  const { userSettings, logout, switchTo } = use(LocaleContext);
  const [showModal, setShowModal] = useState(false);

  const handleOpen = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  const navigate = useNavigate();

  const openSettings = () => {
    navigate('/settings');
  };

  const signOutDialog = (
    <Dialog.Root open={showModal} onOpenChange={setShowModal}>
      <Dialog.Content>
        <Dialog.Title>{t('nav.signOutDialog.title')}</Dialog.Title>
        <Dialog.Description>{t('nav.signOutDialog.description')}</Dialog.Description>

        <div className="signout-dialog-actions">
          <div className="signout-dialog-action">
            <Button
              variant="outline"
              size="3"
              className="w-full signout-dialog-btn signout-dialog-btn-keep"
              onClick={handleClose}
            >
              {t('nav.signOutDialog.cancelButton')}
            </Button>
          </div>
          <div className="signout-dialog-action">
            <Button
              variant="outline"
              size="3"
              className="w-full signout-dialog-btn signout-dialog-btn-signout"
              onClick={() => logout?.()}
            >
              {t('nav.signOutDialog.confirmButton')}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );

  if (asSubmenu) {
    return (
      <>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="dropdown-link flex items-center">
            <UserIcon size={18} className="mr-2" /> {t('nav.userMenu.userSettings')}
          </DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            <DropdownMenu.Label>
              <Text color="gray" highContrast weight="bold">
                {userSettings.firstName} {userSettings.lastName}
              </Text>
            </DropdownMenu.Label>

            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={openSettings}>{t('nav.userMenu.settings')}</DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={(event) => {
                event.preventDefault();
                handleOpen();
              }}
            >
              {t('nav.userMenu.signOut')}
            </DropdownMenu.Item>
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
        {signOutDialog}
      </>
    );
  }

  const actAs =
    userSettings.canActAs && userSettings.canActAs.length > 0 ? (
      <DropdownMenu.Label>
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger className="dropdown-link flex items-center">
            <UserIcon size={18} className="mr-2" /> {t('nav.userMenu.switchTo')}
          </DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            {userSettings.canActAs.map((x) => (
              <DropdownMenu.Item key={x.id} onClick={switchTo!(x.id)!}>
                {x.email}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
      </DropdownMenu.Label>
    ) : undefined;

  return (
    <div className="mx-2">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger style={{ backgroundColor: 'transparent' }}>
          <IconButton color="gray" highContrast aria-label={t('nav.userMenu.userSettings')} className="cursor-pointer">
            <UserIcon />
            <DropdownMenu.TriggerIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content color="gray" variant="soft" align="end">
          <DropdownMenu.Label>
            <Text color="gray" highContrast weight="bold">
              {userSettings.firstName} {userSettings.lastName}
            </Text>
          </DropdownMenu.Label>
          <DropdownMenu.Separator />
          {actAs}
          <DropdownMenu.Item onClick={openSettings}>{t('nav.userMenu.settings')}</DropdownMenu.Item>
          <DropdownMenu.Item onClick={handleOpen}>{t('nav.userMenu.signOut')}</DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {signOutDialog}
    </div>
  );
};

export default User;
